// #region Imports

import { Segment, Table } from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';

import ComptantTable from '@/components/Encaissement/Fiche/Panel/ComptantTable';
import type { LigneComptant } from '@/components/Encaissement/Fiche/Panel/comptant';
import type { DataReglementType, FactureType, LigneRecuType, RecuEncaissementType } from '@/types';
import type { ProduitsListeItemType } from '@/types/produit';
import { grouperLignesParProduit, remapLignesRecu } from '@/utils';
import ModalRecuTableBodyRow from './ModalRecuTableBodyRow';
import ModalRecuTableFooter from './ModalRecuTableFooter';
import ModalRecuTableHeader from './ModalRecuTableHeader';

// #endregion

// #region Types

interface ModalRecuTableProps {
  reglements: DataReglementType[];
  factures: FactureType[];
  selectedItems: (number | string)[];
  lignes: LigneRecuType[];
  recusAnterieurs: RecuEncaissementType[];
  annule?: boolean;
}

// #endregion

const ModalRecuTable = (props: ModalRecuTableProps) => {
  const { reglements, selectedItems, factures, lignes, recusAnterieurs, annule = false } = props;

  // #region Declaration

  // Regroupement des lignes par libellé produit + remapping des lignesRecu
  const { facturesGroupees, lignesRemapped, recusAnterieursRemapped } = React.useMemo(() => {
    let globalMapping: Record<number, number> = {};

    const newFactures = _.map(factures, (facture) => {
      const { lignesGroupees, mapping } = grouperLignesParProduit(facture.lignes);
      globalMapping = { ...globalMapping, ...mapping };
      return { ...facture, lignes: lignesGroupees };
    });

    return {
      facturesGroupees: newFactures,
      lignesRemapped: remapLignesRecu(lignes, globalMapping),
      recusAnterieursRemapped: _.map(recusAnterieurs, (recu) => ({
        ...recu,
        lignes: remapLignesRecu(recu.lignes, globalMapping),
      })),
    };
  }, [factures, lignes, recusAnterieurs]);

  /**
   * Reçu issu d'un encaissement au comptant : les factures sont rattachées au cycle technique
   * de la régie. Ni le cycle ni sa date limite n'ont de sens à présenter, en revanche la
   * quantité saisie par produit, elle, en a.
   */
  const comptant = React.useMemo(
    () => _.some(factures, (facture) => facture?.cycle?.comptant === true),
    [factures],
  );

  /**
   * Reçu au comptant : la facture, ses lignes et le reçu sont créés en une seule transaction,
   * il n'y a donc jamais ni règlement partiel ni reçu antérieur à présenter. On reconstitue
   * simplement le récapitulatif « payeur / produits / quantité / prix unitaire / montant » tel
   * que saisi, réutilisé tel quel depuis la table de saisie du comptant.
   */
  const comptantPayeur = React.useMemo(() => factures[0]?.payeur ?? null, [factures]);

  const comptantLignesFacture = React.useMemo(() => _.flatMap(factures, 'lignes'), [factures]);

  const comptantLignes: LigneComptant[] = React.useMemo(
    () =>
      _.map(comptantLignesFacture, (ligne) => ({
        key: String(ligne.identifiant),
        produit: {
          ...ligne.produit,
          prixUnitaireTTC: ligne.montant / (ligne.quantite || 1),
        } as ProduitsListeItemType,
        quantite: ligne.quantite ?? 1,
      })),
    [comptantLignesFacture],
  );

  const comptantTotal = React.useMemo(
    () => _.sumBy(comptantLignesFacture, 'montant'),
    [comptantLignesFacture],
  );

  // État de visibilité de la colonne produits et montants
  const [produitsMontants, setProduitsMontants] = React.useState(true);
  const [resteEncaisser, setResteEncaisser] = React.useState(false);

  const totalReglements = React.useMemo(() => _.sumBy(reglements, 'montant'), [reglements]);

  const totalResteLignesFactures = React.useMemo(() => {
    const totalLignes = _.sumBy(lignes, 'montant');
    const totalLignesRecuAnterieur = _.sumBy(recusAnterieurs, (recu) =>
      _.sumBy(recu.lignes, 'montant'),
    );
    const totalFacture = _.sumBy(factures, (facture) => _.sumBy(facture.lignes, 'montant'));

    return totalFacture - totalLignes - totalLignesRecuAnterieur;
  }, [factures, lignes, recusAnterieurs]);

  const complete = React.useMemo(() => totalResteLignesFactures <= 0, [totalResteLignesFactures]);

  // #endregion

  // #region Fonctions

  React.useEffect(() => {
    setResteEncaisser(!complete);
  }, [complete]);

  // Fonction pour masquer les produits et montants
  const toggleProduitsMontants = () => setProduitsMontants(!produitsMontants);

  // Fonction pour masquer le reste à encaisser
  const toggleResteEncaisser = () => setResteEncaisser(!resteEncaisser);

  // #endregion

  // Colspan du footer classique uniquement : au comptant, ModalRecuTableFooter n'est jamais rendu.
  const colspanFooter = React.useMemo(() => {
    // N° de facture, payeur, cycle, date limite et montant facture.
    let colspan = 5;

    if (produitsMontants) colspan++;
    if (!_.isEmpty(recusAnterieurs)) colspan++;

    return colspan;
  }, [produitsMontants, recusAnterieurs]);

  return (
    <Segment
      style={{
        padding: 0,
        marginBottom: 0,
        maxHeight: 350,
        // Pour éviter le resize horizontal à chaque fois
        overflowY: 'scroll',
      }}
    >
      {comptant ? (
        <ComptantTable
          readOnly
          payeur={comptantPayeur}
          lignes={comptantLignes}
          total={comptantTotal}
          montantLabel="Montant encaissé"
          totalLabel="Total encaissé"
        />
      ) : (
        <Table celled striped style={{ borderTop: 0, borderBottom: 0, borderLeft: 0 }}>
          <ModalRecuTableHeader
            complete={complete}
            produitsMontants={produitsMontants}
            recusAnterieurs={!_.isEmpty(recusAnterieurs)}
            resteEncaisser={resteEncaisser}
            toggleResteEncaisser={toggleResteEncaisser}
            toggleProduitsMontants={toggleProduitsMontants}
          />
          <Table.Body>
            {_.map(facturesGroupees, (facture) => (
              <ModalRecuTableBodyRow
                facture={facture}
                lignesRecu={lignesRemapped}
                resteEncaisser={resteEncaisser}
                produitsMontants={produitsMontants}
                reglements={reglements}
                selectedItems={selectedItems}
                recusAnterieurs={recusAnterieursRemapped}
              />
            ))}
          </Table.Body>
          <ModalRecuTableFooter
            totalReste={totalResteLignesFactures}
            totalReglements={totalReglements}
            colspanFooter={colspanFooter}
            resteEncaisser={resteEncaisser}
            reglements={reglements}
            selectedItems={selectedItems}
            annule={annule}
          />
        </Table>
      )}
    </Segment>
  );
};

export default ModalRecuTable;
